import { useEffect } from 'react';
import TankersEventFactory from '../factory/TankersEventFactory';
import { TankersEvent } from '../types/ViewModel/TankersEvent';
import { MoveEventsPortCall } from '../types/ViewModel/MoveEventsPortCall';
import { MoveEventsPortCallFactory } from '../factory/MoveEventsPortCallFactory';
import { MoveEventsToCargoOperationFactory } from '../factory/MoveEventsToCargoOperationFactory';
import { MoveEventsToBerthCallFactory } from '../factory/MoveEventsToBerthCallFactory';
import { useAuthStore } from '../context/AuthContext';
import { useEventStore } from '../context/EventContext';
import { useRotationStore } from '../context/RotationContext';
import queryParamsUtils from '../utils/queryParamsUtils';
import useEvent from '../app/useEvent';
import useEventType from '../app/useEventType';
import { useEventTypeStore } from '../context/EventTypeContext';
import useApi from './useApi';
import useApiEndpoints from '../context/useApiEndpoints';
import { buildQueryParams } from './endpoints/queryParamsHelper';
import { formatDateProperties } from 'odfjell-date-time-utils/dateTimeFormatter';
import { getUtcTimeNow } from 'odfjell-date-time-utils/dateTimeUtils';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const moveEventsPortCallFactory = new MoveEventsPortCallFactory();
    const moveEventsToBerthCallFactory = new MoveEventsToBerthCallFactory();
    const moveEventsToCargoOperationFactory = new MoveEventsToCargoOperationFactory();
    const { state, postApiData } = useApi();
    const { createTankersEventBM, createNewEvent } = TankersEventFactory();
    const { signalRConnectionId, loggedInUser } = useAuthStore();
    const { setSavedEvent, setLastEventChangeDate } = useEventStore();
    const { eventTypeDataPort } = useEventTypeStore();
    const { processApiEndpointError } = apiEndpointUtils();
    const { rotation } = useRotationStore();
    const { createPortCallListQueryParam, setQueryParamEvent } = queryParamsUtils();
    const { addEventToList, updateEventInList, moveEventsToCargoOperationUpdate, moveEventsToBerthCallUpdate, moveEventsToPortCallUpdate } =
        useEvent();
    const { getEventTypeDataVersion, getMoveEventTypeMapList } = useEventType();

    const isOfflineResponse = (result: any) => result?.isOfflineResponse && result.eventId;

    useEffect(() => {
        if (state.status === 'success') {
            if (state.endpoint === apiEndpoints['createEvent'].url) {
                setSavedEvent(state.additionalInfo.tankersEvent);
                setQueryParamEvent(state.additionalInfo.tankersEvent.eventId!.toString());
                if (state.result === 1 || isOfflineResponse(state.result)) {
                    addEventToList(state.additionalInfo.tankersEvent);
                    setLastEventChangeDate(getUtcTimeNow());
                }
            } else if (state.result === 1 || isOfflineResponse(state.result)) {
                if (state.endpoint === apiEndpoints['updateEvent'].url) {
                    updateEventInList(state.additionalInfo.tankersEvent);
                    setLastEventChangeDate(getUtcTimeNow());
                }
                if (state.endpoint === apiEndpoints['moveEventsToCargoOperation'].url)
                    moveEventsToCargoOperationUpdate(state.additionalInfo.moveEvCo);
                if (state.endpoint === apiEndpoints['moveEventsToBerthCall'].url) moveEventsToBerthCallUpdate(state.additionalInfo.moveEvBc);
                if (state.endpoint === apiEndpoints['moveEventsToPortCall'].url) moveEventsToPortCallUpdate(state.additionalInfo.moveEvPc);
            }
        } else {
            processApiEndpointError(state);
        }
    }, [state]);

    const storeEvent = async (tankersEvent: TankersEvent) => {
        const ev = createNewEvent(tankersEvent);
        formatDateProperties(ev);
        formatDateProperties(ev.eventDate);
        const evTypeDataVersion = getEventTypeDataVersion(eventTypeDataPort);
        if (evTypeDataVersion) {
            ev.eventTypeDataVersion = evTypeDataVersion.version;
        }
        ev.eventTypeInputRegList?.filter((o) => o.datetimeValueReg !== undefined).forEach((o) => formatDateProperties(o));
        const tankersEventBM = createTankersEventBM(ev);
        tankersEventBM.updatedBy = loggedInUser!.userName;
        tankersEvent.updatedBy = loggedInUser!.userName;

        const result = await postApiData({
            endpoint: apiEndpoints['createEvent'].url,
            data: tankersEventBM,
            queryParam: buildQueryParams({ connectionId: signalRConnectionId, imoNumber: tankersEvent.shipId }),
            additionalInfo: { tankersEvent },
        });

        return result;
    };

    const updateEvent = async (tankersEvent: TankersEvent) => {
        const ev = createNewEvent(tankersEvent);
        formatDateProperties(ev);
        formatDateProperties(ev.eventDate);
        ev.eventTypeInputRegList?.filter((o) => o.datetimeValueReg !== undefined).forEach((o) => formatDateProperties(o));
        const tankersEventBM = createTankersEventBM(ev);
        tankersEventBM.updatedBy = loggedInUser!.userName;
        tankersEvent.updatedBy = loggedInUser!.userName;

        const portCallIdList = createPortCallListQueryParam(rotation);
        const result = await postApiData({
            endpoint: apiEndpoints['updateEvent'].url,
            data: tankersEventBM,
            queryParam: buildQueryParams({ connectionId: signalRConnectionId, portCallIdList, imoNumber: tankersEvent.shipId }),
            additionalInfo: { tankersEvent },
        });
        return result;
    };

    const moveEventsToCargoOperation = async (
        imoNumber: number,
        cargoOperationId: number,
        portCallId: number,
        fromBerthCallId: number,
        toBerthCallId: number,
        toBerthId: number
    ) => {
        const moveEvCo = moveEventsToCargoOperationFactory.createMoveEventsToCargoOperationBM(
            imoNumber,
            portCallId,
            cargoOperationId,
            fromBerthCallId,
            toBerthCallId,
            toBerthId,
            loggedInUser!.userName,
            getMoveEventTypeMapList(eventTypeDataPort)
        );
        const portCallIdList = createPortCallListQueryParam(rotation);
        const result = await postApiData({
            endpoint: apiEndpoints['moveEventsToCargoOperation'].url,
            data: moveEvCo,
            queryParam: buildQueryParams({ imoNumber, portCallIdList, connectionId: signalRConnectionId }),
            additionalInfo: { moveEvCo },
        });
        return result;
    };

    const moveEventsToBerthCall = async (
        imoNumber: number,
        portCallId: number,
        fromBerthCallId: number,
        toBerthCallId: number,
        toBerthId: number | null,
        toBerthSequenceNumber: number | null
    ) => {
        const moveEvBc = moveEventsToBerthCallFactory.createMoveEventsToBerthCallBM(
            imoNumber,
            portCallId,
            fromBerthCallId,
            toBerthCallId,
            toBerthId,
            toBerthSequenceNumber,
            loggedInUser!.userName
        );
        const portCallIdList = createPortCallListQueryParam(rotation);
        const result = await postApiData({
            endpoint: apiEndpoints['moveEventsToBerthCall'].url,
            data: moveEvBc,
            queryParam: buildQueryParams({ imoNumber, portCallIdList, connectionId: signalRConnectionId }),
            additionalInfo: { moveEvBc },
        });
        return result;
    };

    const moveEventsToPortCall = async (imoNumber: number, moveEventsPortCall: MoveEventsPortCall) => {
        const moveEvPc = moveEventsPortCallFactory.createMoveEventsPortCallBM(moveEventsPortCall, loggedInUser!.userName);
        const portCallIdList = createPortCallListQueryParam(rotation);
        const result = await postApiData({
            endpoint: apiEndpoints['moveEventsToPortCall'].url,
            data: moveEvPc,
            queryParam: buildQueryParams({ imoNumber, portCallIdList, connectionId: signalRConnectionId }),
            additionalInfo: { moveEvPc },
        });
        return result;
    };

    return {
        storeEvent,
        updateEvent,
        moveEventsToCargoOperation,
        moveEventsToBerthCall,
        moveEventsToPortCall,
    };
};
